import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './CardDetailModal.css';
import AppIcon from './icons/AppIcon';

export default function CardDetailModal({ card, listId, lists, onClose, onUpdate, onDelete }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [cardData, setCardData] = useState(card);
  const [newComment, setNewComment] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [companyAgents, setCompanyAgents] = useState([]);
  const [newLink, setNewLink] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [reminderMinutes, setReminderMinutes] = useState(0);
  const [isAllDay, setIsAllDay] = useState(false);

  const handleSave = () => {
    onUpdate(listId, cardData);
  };

  const handleAddLabel = (label) => {
    const updatedLabels = [...(cardData.labels || []), label];
    const updated = { ...cardData, labels: updatedLabels };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  const handleRemoveLabel = (index) => {
    const updatedLabels = cardData.labels.filter((_, i) => i !== index);
    const updated = { ...cardData, labels: updatedLabels };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;

    const checklist = cardData.checklist || [];
    const newItem = {
      id: Date.now().toString(),
      title: newChecklistItem,
      completed: false,
      assignee: null,
      dueDate: null
    };

    const updated = { ...cardData, checklist: [...checklist, newItem] };
    setCardData(updated);
    onUpdate(listId, updated);
    setNewChecklistItem('');
  };

  const handleToggleChecklistItem = (itemId) => {
    const checklist = cardData.checklist.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    const updated = { ...cardData, checklist };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  const handleDeleteChecklistItem = (itemId) => {
    const checklist = cardData.checklist.filter(item => item.id !== itemId);
    const updated = { ...cardData, checklist };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      // Buscar nome do usuário do banco de dados
      const { data: profile } = await supabase
        .from('profiles')
        .select('nome, email')
        .eq('id', user.id)
        .single();

      const authorName = profile?.nome || user?.nome || user?.email?.split('@')[0] || 'Usuário';
      
      console.log('👤 Autor do comentário:', authorName);

      const comments = cardData.comments || [];
      const newCommentObj = {
        id: Date.now().toString(),
        text: newComment,
        author: authorName,
        author_id: user?.id,
        createdAt: new Date().toISOString()
      };

      const updated = { ...cardData, comments: [...comments, newCommentObj] };
      setCardData(updated);
      onUpdate(listId, updated);
      setNewComment('');
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      alert('Erro ao adicionar comentário');
    }
  };

  const handleMoveCard = (newListId) => {
    onUpdate(newListId, cardData, listId);
    onClose();
  };

  const handleSetPriority = (priority) => {
    const updated = { ...cardData, priority };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  // Carregar membros da empresa
  useEffect(() => {
    loadCompanyMembers();
    loadCompanyAgents();
  }, []);

  const loadCompanyMembers = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, nome_usuario, email')
        .eq('company_id', user.company_id);

      if (error) throw error;
      setCompanyMembers(data || []);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
    }
  };

  const loadCompanyAgents = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      const { data, error } = await supabase
        .from('agentes')
        .select('id, nome, funcao_principal')
        .eq('empresa_id', user.company_id);

      if (error) throw error;
      
      // Mapear funcao_principal para funcao
      const mappedData = (data || []).map(agent => ({
        ...agent,
        funcao: agent.funcao_principal
      }));
      
      setCompanyAgents(mappedData);
    } catch (error) {
      console.error('Erro ao carregar agentes:', error);
    }
  };

  const handleToggleMember = (memberId) => {
    const members = cardData.members || [];
    const isMember = members.includes(memberId);
    
    const updatedMembers = isMember
      ? members.filter(id => id !== memberId)
      : [...members, memberId];
    
    const updated = { ...cardData, members: updatedMembers };
    setCardData(updated);
  };

  const handleSetDueDate = async () => {
    try {
      if (!eventDate) {
        alert('Por favor, selecione uma data');
        return;
      }

      if (!isAllDay && !eventTime) {
        alert('Por favor, selecione uma hora ou marque como "Dia inteiro"');
        return;
      }

      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      // Combinar data e hora
      let fullDateTime = eventDate;
      if (isAllDay) {
        fullDateTime = `${eventDate}T00:00:00`;
      } else {
        fullDateTime = `${eventDate}T${eventTime}:00`;
      }

      // Atualizar card com data e lembrete
      const updated = { 
        ...cardData, 
        dueDate: fullDateTime,
        reminder: reminderMinutes > 0 ? reminderMinutes : null
      };
      setCardData(updated);

      // Verificar se já existe evento para este card
      const { data: existingEvent } = await supabase
        .from('calendar_events')
        .select('id')
        .eq('project_item_id', cardData.id)
        .single();

      // Preparar lembretes
      const reminders = [];
      if (reminderMinutes > 0) {
        reminders.push({
          type: 'notification',
          minutes_before: reminderMinutes
        });
      }

      console.log('Salvando lembrete:', {
        reminderMinutes,
        reminders,
        fullDateTime
      });

      const eventData = {
        company_id: user.company_id,
        title: `📌 ${cardData.title}`,
        description: cardData.description || '',
        start_date: fullDateTime,
        end_date: fullDateTime,
        all_day: isAllDay,
        event_type: 'deadline',
        priority: cardData.priority || 'normal',
        project_item_id: cardData.id,
        reminders: reminders, // Supabase aceita array diretamente
        created_by: user.id
      };

      if (existingEvent) {
        // Atualizar evento existente
        const { error } = await supabase
          .from('calendar_events')
          .update(eventData)
          .eq('id', existingEvent.id);
        
        if (error) throw error;
      } else {
        // Criar novo evento
        const { error } = await supabase
          .from('calendar_events')
          .insert(eventData);
        
        if (error) throw error;
      }
      
      // Limpar campos
      setEventDate('');
      setEventTime('');
      setReminderMinutes(0);
      setIsAllDay(false);
      setShowDatePicker(false);
      
      alert('Data e lembrete salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar data:', error);
      alert('Erro ao salvar data: ' + error.message);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingFile(true);
      
      // Converter arquivo para base64 ou usar URL temporária
      const reader = new FileReader();
      reader.onloadend = () => {
        const attachments = cardData.attachments || [];
        const newAttachment = {
          id: Date.now().toString(),
          name: file.name,
          url: reader.result, // Base64 data URL
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString()
        };

        const updated = { ...cardData, attachments: [...attachments, newAttachment] };
        setCardData(updated);
        onUpdate(listId, updated);
        setUploadingFile(false);
      };
      
      reader.onerror = () => {
        throw new Error('Erro ao ler arquivo');
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload: ' + error.message);
      setUploadingFile(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLink.trim()) return;

    try {
      // Validar e adicionar protocolo se necessário
      let validUrl = newLink.trim();
      
      // Remover espaços e caracteres inválidos
      validUrl = validUrl.replace(/\s+/g, '');
      
      // Adicionar https:// se não tiver protocolo
      if (!validUrl.match(/^https?:\/\//i)) {
        validUrl = 'https://' + validUrl;
      }

      console.log('🔗 Buscando preview para:', validUrl);

      // Buscar preview do link via backend
      const response = await fetch('https://api.halo.ia.br/api/link-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: validUrl })
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Erro do servidor:', errorData);
        throw new Error(errorData.error || 'Erro ao buscar preview');
      }

      const preview = await response.json();
      console.log('✅ Preview recebido:', preview);
      console.log('🖼️ Imagem do preview:', preview.image);

      const links = cardData.links || [];
      const newLinkObj = {
        id: Date.now().toString(),
        url: preview.url || validUrl,
        title: preview.title || validUrl,
        description: preview.description || '',
        image: preview.image || null,
        favicon: preview.favicon || `https://www.google.com/s2/favicons?domain=${validUrl}&sz=64`,
        addedAt: new Date().toISOString()
      };

      console.log('💾 Salvando link:', newLinkObj);
      console.log('🖼️ Link tem imagem?', !!newLinkObj.image, newLinkObj.image);

      const updated = { ...cardData, links: [...links, newLinkObj] };
      setCardData(updated);
      onUpdate(listId, updated);
      setNewLink('');
      setShowLinkModal(false);
    } catch (error) {
      console.error('❌ Erro completo:', error);
      alert('Erro ao adicionar link: ' + error.message);
    }
  };

  const handleRemoveAttachment = (attachmentId) => {
    const attachments = cardData.attachments.filter(a => a.id !== attachmentId);
    const updated = { ...cardData, attachments };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  const handleRemoveLink = (linkId) => {
    const links = cardData.links.filter(l => l.id !== linkId);
    const updated = { ...cardData, links };
    setCardData(updated);
    onUpdate(listId, updated);
  };

  const handleToggleAgent = (agentId) => {
    const agents = cardData.agents || [];
    const hasAgent = agents.includes(agentId);
    
    const updatedAgents = hasAgent
      ? agents.filter(id => id !== agentId)
      : [...agents, agentId];
    
    const updated = { ...cardData, agents: updatedAgents };
    setCardData(updated);
  };

  const priorityColors = {
    baixa: '#10b981',
    normal: '#3b82f6',
    alta: '#f59e0b',
    urgente: '#ef4444',
    critica: '#dc2626'
  };

  const checklistProgress = cardData.checklist?.length > 0
    ? Math.round((cardData.checklist.filter(i => i.completed).length / cardData.checklist.length) * 100)
    : 0;

  return (
    <div className="modal-overlay-card" onClick={onClose}>
      <div className="card-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="card-modal-header">
          <div className="card-modal-title-section">
            <AppIcon name="credit_card" className="card-modal-icon" />
            {editingTitle ? (
              <input
                type="text"
                value={cardData.title}
                onChange={(e) => setCardData({ ...cardData, title: e.target.value })}
                onBlur={() => {
                  setEditingTitle(false);
                  handleSave();
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setEditingTitle(false);
                    handleSave();
                  }
                }}
                className="card-title-input-modal"
                autoFocus
              />
            ) : (
              <h2 onClick={() => setEditingTitle(true)}>{cardData.title}</h2>
            )}
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="card-modal-content">
          {/* Coluna Principal */}
          <div className="card-modal-main">
            {/* Labels */}
            {cardData.labels && cardData.labels.length > 0 && (
              <div className="card-section">
                <h4>
                  <AppIcon name="label" />
                  Etiquetas
                </h4>
                <div className="card-labels-list">
                  {cardData.labels.map((label, idx) => (
                    <span
                      key={idx}
                      className="card-label-modal"
                      style={{ backgroundColor: label.color }}
                    >
                      {label.name}
                      <button
                        className="btn-remove-label"
                        onClick={() => handleRemoveLabel(idx)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Data e Hora */}
            {cardData.dueDate && (
              <div className="card-section">
                <h4>
                  <AppIcon name="schedule" />
                  Data e Hora
                </h4>
                <div className="due-date-display">
                  <div className="due-date-info">
                    <AppIcon name="calendar_today" />
                    <span>
                      {new Date(cardData.dueDate).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="due-date-info">
                    <AppIcon name="access_time" />
                    <span>
                      {new Date(cardData.dueDate).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {cardData.reminder && (
                    <div className="due-date-info reminder-info">
                      <AppIcon name="notifications" />
                      <span>
                        {cardData.reminder < 60 
                          ? `${cardData.reminder} min antes`
                          : cardData.reminder < 1440
                          ? `${Math.floor(cardData.reminder / 60)}h antes`
                          : `${Math.floor(cardData.reminder / 1440)} dia(s) antes`
                        }
                      </span>
                    </div>
                  )}
                  <button 
                    className="btn-edit-date"
                    onClick={() => {
                      const date = new Date(cardData.dueDate);
                      setEventDate(date.toISOString().split('T')[0]);
                      setEventTime(date.toTimeString().slice(0, 5));
                      setReminderMinutes(cardData.reminder || 0);
                      setShowDatePicker(true);
                    }}
                  >
                    <AppIcon name="edit" />
                    Editar
                  </button>
                </div>
              </div>
            )}

            {/* Descrição */}
            <div className="card-section">
              <h4>
                <AppIcon name="subject" />
                Descrição
              </h4>
              {editingDescription ? (
                <textarea
                  value={cardData.description || ''}
                  onChange={(e) => setCardData({ ...cardData, description: e.target.value })}
                  onBlur={() => {
                    setEditingDescription(false);
                    handleSave();
                  }}
                  className="card-description-textarea"
                  rows="5"
                  placeholder="Adicione uma descrição mais detalhada..."
                  autoFocus
                />
              ) : (
                <div
                  className="card-description-display"
                  onClick={() => setEditingDescription(true)}
                >
                  {cardData.description || 'Adicione uma descrição mais detalhada...'}
                </div>
              )}
            </div>

            {/* Checklist */}
            <div className="card-section">
              <div className="section-header-with-action">
                <h4>
                  <AppIcon name="checklist" />
                  Checklist
                </h4>
                {cardData.checklist && cardData.checklist.length > 0 && (
                  <span className="checklist-progress-text">
                    {checklistProgress}%
                  </span>
                )}
              </div>

              {cardData.checklist && cardData.checklist.length > 0 && (
                <>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${checklistProgress}%` }}
                    />
                  </div>

                  <div className="checklist-items">
                    {cardData.checklist.map(item => (
                      <div key={item.id} className="checklist-item">
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={() => handleToggleChecklistItem(item.id)}
                          className="checklist-checkbox"
                        />
                        <span className={item.completed ? 'completed' : ''}>
                          {item.title}
                        </span>
                        <button
                          className="btn-delete-checklist-item"
                          onClick={() => handleDeleteChecklistItem(item.id)}
                        >
                          <AppIcon name="delete" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {showAddChecklist ? (
                <div className="add-checklist-form">
                  <input
                    type="text"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleAddChecklistItem();
                    }}
                    placeholder="Adicionar um item..."
                    className="checklist-input"
                    autoFocus
                  />
                  <div className="checklist-form-actions">
                    <button className="btn-add-item" onClick={handleAddChecklistItem}>
                      Adicionar
                    </button>
                    <button
                      className="btn-cancel-item"
                      onClick={() => {
                        setShowAddChecklist(false);
                        setNewChecklistItem('');
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-add-checklist"
                  onClick={() => setShowAddChecklist(true)}
                >
                  <AppIcon name="add" />
                  Adicionar item
                </button>
              )}
            </div>

            {/* Anexos */}
            {cardData.attachments && cardData.attachments.length > 0 && (
              <div className="card-section">
                <h4>
                  <AppIcon name="attach_file" />
                  Anexos
                </h4>
                <div className="attachments-list">
                  {cardData.attachments.map((attachment) => (
                    <div key={attachment.id} className="attachment-item-preview">
                      {attachment.type?.startsWith('image/') ? (
                        <img src={attachment.url} alt={attachment.name} className="attachment-image-preview" />
                      ) : (
                        <div className="attachment-file-icon">
                          <AppIcon name="description" />
                        </div>
                      )}
                      <div className="attachment-info">
                        <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                          {attachment.name}
                        </a>
                        <span className="attachment-size">
                          {(attachment.size / 1024).toFixed(2)} KB
                        </span>
                      </div>
                      <button
                        className="btn-remove-attachment"
                        onClick={() => handleRemoveAttachment(attachment.id)}
                      >
                        <AppIcon name="close" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {cardData.links && cardData.links.length > 0 && (
              <div className="card-section">
                <h4>
                  <AppIcon name="link" />
                  Links
                </h4>
                <div className="links-list">
                  {cardData.links.map((link) => (
                    <div key={link.id} className="link-item-preview">
                      {link.image && (
                        <div className="link-preview-image">
                          <img 
                            src={link.image} 
                            alt={link.title}
                            onError={(e) => {
                              e.target.parentElement.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="link-preview-content">
                        {link.favicon && (
                          <img 
                            src={link.favicon} 
                            alt="" 
                            className="link-favicon"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        )}
                        <AppIcon name="language" className="link-icon-fallback" />
                        <div className="link-info">
                          <a 
                            href={link.url.startsWith('http') ? link.url : `https://${link.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="link-title"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {link.title}
                          </a>
                          {link.description && (
                            <span className="link-description">{link.description}</span>
                          )}
                          <span className="link-url">{link.url}</span>
                        </div>
                      </div>
                      <button
                        className="btn-remove-link"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveLink(link.id);
                        }}
                      >
                        <AppIcon name="close" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comentários */}
            <div className="card-section">
              <h4>
                <AppIcon name="comment" />
                Comentários
              </h4>

              <div className="add-comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escrever um comentário..."
                  className="comment-textarea"
                  rows="3"
                />
                <button
                  className="btn-add-comment"
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                >
                  Comentar
                </button>
              </div>

              {cardData.comments && cardData.comments.length > 0 && (
                <div className="comments-list">
                  {cardData.comments.map(comment => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-avatar">
                        {comment.author?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <span className="comment-author">{comment.author || 'Usuário'}</span>
                          <span className="comment-date">
                            {new Date(comment.createdAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })} às {new Date(comment.createdAt).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="comment-text">{comment.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar de Ações */}
          <div className="card-modal-sidebar">
            <h4>Adicionar ao card</h4>

            <button 
              className="sidebar-btn"
              onClick={() => setShowMembersModal(true)}
            >
              <AppIcon name="person" />
              Membros
            </button>

            <button 
              className="sidebar-btn"
              onClick={() => alert('Funcionalidade de etiquetas será implementada')}
            >
              <AppIcon name="label" />
              Etiquetas
            </button>

            <button 
              className="sidebar-btn"
              onClick={() => setShowDatePicker(true)}
            >
              <AppIcon name="schedule" />
              Datas
            </button>

            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <button 
              className="sidebar-btn" 
              onClick={() => document.getElementById('file-upload').click()}
              disabled={uploadingFile}
            >
              <AppIcon name="attach_file" />
              {uploadingFile ? 'Enviando...' : 'Anexo'}
            </button>

            <button 
              className="sidebar-btn"
              onClick={() => setShowLinkModal(true)}
            >
              <AppIcon name="link" />
              Links
            </button>

            <h4 style={{ marginTop: '1.5rem' }}>Automação e IA</h4>

            <button 
              className="sidebar-btn sidebar-btn-highlight"
              onClick={() => alert('Funcionalidade de automações será implementada')}
            >
              <AppIcon name="bolt" />
              Automações
            </button>

            <button 
              className="sidebar-btn sidebar-btn-highlight"
              onClick={() => setShowAgentsModal(true)}
            >
              <AppIcon name="smart_toy" />
              Agentes IA
            </button>

            <h4 style={{ marginTop: '1.5rem' }}>Ações</h4>

            <div className="priority-section">
              <label>Prioridade:</label>
              <select
                value={cardData.priority || 'normal'}
                onChange={(e) => handleSetPriority(e.target.value)}
                className="priority-select"
                style={{ borderColor: priorityColors[cardData.priority || 'normal'] }}
              >
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
                <option value="critica">Crítica</option>
              </select>
            </div>

            <div className="move-section">
              <label>Mover para:</label>
              <select
                value={listId}
                onChange={(e) => handleMoveCard(e.target.value)}
                className="move-select"
              >
                {lists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.title}
                  </option>
                ))}
              </select>
            </div>

            <button 
              className="sidebar-btn btn-save"
              onClick={() => {
                handleSave();
                alert('Card salvo com sucesso!');
              }}
            >
              <AppIcon name="save" />
              Salvar
            </button>

            <button className="sidebar-btn btn-archive">
              <AppIcon name="archive" />
              Arquivar
            </button>

            <button
              className="sidebar-btn btn-delete"
              onClick={() => {
                if (confirm('Deseja realmente excluir este card?')) {
                  onDelete(listId, card.id);
                  onClose();
                }
              }}
            >
              <AppIcon name="delete" />
              Excluir
            </button>
          </div>
        </div>

        {/* Modal de Membros */}
        {showMembersModal && (
          <div className="sub-modal-overlay" onClick={() => setShowMembersModal(false)}>
            <div className="sub-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h3>Membros</h3>
                <button onClick={() => setShowMembersModal(false)}>
                  <AppIcon name="close" />
                </button>
              </div>
              <div className="sub-modal-content">
                <p className="sub-modal-description">Selecione os membros para este card</p>
                <div className="members-list">
                  {companyMembers.map(member => (
                    <div
                      key={member.id}
                      className={`member-item ${(cardData.members || []).includes(member.id) ? 'selected' : ''}`}
                      onClick={() => handleToggleMember(member.id)}
                    >
                      <div className="member-avatar">
                        {member.nome_usuario?.charAt(0) || member.nome?.charAt(0) || 'U'}
                      </div>
                      <div className="member-info">
                        <span className="member-name">{member.nome || member.nome_usuario}</span>
                        <span className="member-email">{member.email}</span>
                      </div>
                      {(cardData.members || []).includes(member.id) && (
                        <AppIcon name="check_circle" className="check-icon" />
                      )}
                    </div>
                  ))}
                </div>
                <button 
                  className="btn-confirm-modal"
                  onClick={() => {
                    handleSave();
                    setShowMembersModal(false);
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Data */}
        {showDatePicker && (
          <div className="sub-modal-overlay" onClick={() => setShowDatePicker(false)}>
            <div className="sub-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h3>Definir Data e Hora</h3>
                <button onClick={() => setShowDatePicker(false)}>
                  <AppIcon name="close" />
                </button>
              </div>
              <div className="sub-modal-content">
                <p className="sub-modal-description">
                  Esta data será salva como um evento no calendário
                </p>
                
                <div className="all-day-checkbox">
                  <input
                    type="checkbox"
                    id="all-day"
                    checked={isAllDay}
                    onChange={(e) => {
                      setIsAllDay(e.target.checked);
                      if (e.target.checked) setEventTime('');
                    }}
                  />
                  <label htmlFor="all-day">Dia inteiro</label>
                </div>
                
                <div className="date-time-group">
                  <div className="input-group">
                    <label>Data *</label>
                    <input
                      type="date"
                      className="date-input"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                    />
                  </div>
                  
                  {!isAllDay && (
                    <div className="input-group">
                      <label>Hora *</label>
                      <input
                        type="time"
                        className="time-input"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                <div className="input-group">
                  <label>Lembrete</label>
                  <select
                    className="reminder-select"
                    value={reminderMinutes}
                    onChange={(e) => setReminderMinutes(Number(e.target.value))}
                  >
                    <option value="0">Sem lembrete</option>
                    <option value="5">5 minutos antes</option>
                    <option value="15">15 minutos antes</option>
                    <option value="30">30 minutos antes</option>
                    <option value="60">1 hora antes</option>
                    <option value="120">2 horas antes</option>
                    <option value="1440">1 dia antes</option>
                    <option value="10080">1 semana antes</option>
                  </select>
                </div>

                <button 
                  className="btn-confirm-modal"
                  onClick={handleSetDueDate}
                >
                  Salvar Data e Lembrete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Link */}
        {showLinkModal && (
          <div className="sub-modal-overlay" onClick={() => setShowLinkModal(false)}>
            <div className="sub-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h3>Adicionar Link</h3>
                <button onClick={() => setShowLinkModal(false)}>
                  <AppIcon name="close" />
                </button>
              </div>
              <div className="sub-modal-content">
                <input
                  type="url"
                  className="link-input"
                  placeholder="https://exemplo.com"
                  value={newLink}
                  onChange={(e) => setNewLink(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleAddLink();
                  }}
                />
                <button 
                  className="btn-confirm-modal"
                  onClick={handleAddLink}
                  disabled={!newLink.trim()}
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Agentes IA */}
        {showAgentsModal && (
          <div className="sub-modal-overlay" onClick={() => setShowAgentsModal(false)}>
            <div className="sub-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sub-modal-header">
                <h3>Agentes de IA</h3>
                <button onClick={() => setShowAgentsModal(false)}>
                  <AppIcon name="close" />
                </button>
              </div>
              <div className="sub-modal-content">
                <p className="sub-modal-description">Selecione os agentes de IA para este card</p>
                <div className="agents-list">
                  {companyAgents.map(agent => (
                    <div
                      key={agent.id}
                      className={`agent-item ${(cardData.agents || []).includes(agent.id) ? 'selected' : ''}`}
                      onClick={() => handleToggleAgent(agent.id)}
                    >
                      <AppIcon name="smart_toy" className="agent-icon" />
                      <div className="agent-info">
                        <span className="agent-name">{agent.nome}</span>
                        <span className="agent-function">{agent.funcao}</span>
                      </div>
                      {(cardData.agents || []).includes(agent.id) && (
                        <AppIcon name="check_circle" className="check-icon" />
                      )}
                    </div>
                  ))}
                </div>
                <button 
                  className="btn-create-agent"
                  onClick={() => alert('Modal de criar agente será implementado')}
                >
                  <AppIcon name="add" />
                  Criar Novo Agente
                </button>
                <button 
                  className="btn-confirm-modal"
                  onClick={() => {
                    handleSave();
                    setShowAgentsModal(false);
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
